import { whatsappLink } from "@/mocks/llkContent";
import { ScrollReveal } from "@/components/animations/ScrollReveal";
import { StaggerContainer, StaggerItem } from "@/components/animations/StaggerContainer";
import { motion, useInView } from "motion/react";
import { useRef } from "react";

const solutionImage = "https://readdy.ai/api/search-image?query=Close%20up%20of%20healthy%20white%20roots%20in%20rich%20dark%20soil%20with%20green%20plant%20base%2C%20agricultural%20biotechnology%20aesthetic%2C%20warm%20natural%20lighting%2C%20premium%20photography%20style%2C%20earthy%20tones%20with%20subtle%20green%20accents%2C%20shallow%20depth%20of%20field%2C%20no%20text&width=800&height=600&seq=llk-roots&orientation=landscape";

const listVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12, delayChildren: 0.3 },
  },
};

const listItemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] },
  },
};

export default function SolutionSection() {
  const imageRef = useRef(null);
  const imageInView = useInView(imageRef, { once: true, amount: 0.3 });

  return (
    <section id="solucao" className="relative py-16 sm:py-20 md:py-28 bg-llk-cream-2">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8">
        <div className="flex flex-col lg:flex-row items-center gap-8 sm:gap-10 lg:gap-16">
          {/* Text */}
          <div className="flex-1 order-2 lg:order-1">
            <ScrollReveal>
              <p className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-llk-gold-dark mb-3 sm:mb-4">
                NOSSA ABORDAGEM
              </p>
            </ScrollReveal>

            <ScrollReveal delay={0.1}>
              <h2 className="font-display text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-llk-dark font-semibold leading-tight mb-4 sm:mb-6">
                A LLK entrega mais do que produtos.
                <br className="hidden sm:block" />
                <span className="italic text-llk-earth"> Entrega estratégia biológica para o campo.</span>
              </h2>
            </ScrollReveal>

            <ScrollReveal delay={0.2}>
              <p className="text-llk-dark/70 text-sm sm:text-base md:text-lg leading-relaxed mb-6 sm:mb-8">
                Nossa linha atua em pontos decisivos: proteção radicular, biocontrole, solubilização de nutrientes, estímulo vegetal e eficiência dos biológicos na aplicação. Com abordagem consultiva, indicamos a tecnologia mais adequada para cada cultura, fase e desafio.
              </p>
            </ScrollReveal>

            <motion.div
              ref={imageRef}
              variants={listVariants}
              initial="hidden"
              animate={imageInView ? "visible" : "hidden"}
              className="flex flex-col gap-3 sm:gap-4"
            >
              {[
                "Proteção radicular e biocontrole",
                "Solubilização de nutrientes e estímulo vegetal",
                "Eficiência dos biológicos na aplicação",
              ].map((item) => (
                <motion.div key={item} variants={listItemVariants} className="flex items-start gap-3">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-llk-gold/20 flex items-center justify-center mt-0.5 shrink-0">
                    <i className="ri-check-line text-llk-gold text-xs sm:text-sm" />
                  </div>
                  <span className="text-llk-dark/80 text-sm sm:text-base">{item}</span>
                </motion.div>
              ))}
            </motion.div>

            <ScrollReveal delay={0.4}>
              <div className="mt-6 sm:mt-8">
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 sm:py-3 bg-llk-dark text-white text-xs sm:text-sm font-medium rounded-full hover:bg-llk-gold transition-colors whitespace-normal sm:whitespace-nowrap w-full sm:w-auto"
                >
                  <i className="ri-whatsapp-line" />
                  Quero entender qual solução usar na minha lavoura
                </a>
              </div>
            </ScrollReveal>
          </div>

          {/* Image */}
          <ScrollReveal direction="right" delay={0.2} className="flex-1 order-1 lg:order-2 w-full">
            <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl sm:shadow-2xl">
              <img
                src={solutionImage}
                alt="Raízes saudáveis em solo fértil"
                className="w-full h-auto object-cover aspect-[4/3] sm:aspect-auto"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-llk-dark/30 to-transparent" />
              <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6">
                <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/90 backdrop-blur-sm">
                  <i className="ri-seedling-line text-llk-green-soft text-sm" />
                  <span className="text-llk-dark text-xs sm:text-sm font-medium">Saúde Radicular</span>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}